import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { api } from "@/lib/api"
import { resolvePublishedRevision, revisionForSelection, type PublishedRevision } from "@/lib/calculator-revision"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  IconCalculator,
  IconLoader2,
  IconCheck,
  IconX,
  IconCurrencyDollar,
} from "@tabler/icons-react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BenefitProgramSummary {
  id: number
  name: string
  description: string
  availableProgramYears: number[]
}

interface ProgramYearSettingsMetadata {
  programYear: number
  revision: number
  startDate: string
}

interface ProvidedService {
  service: { id: number; categoryId: number }
  provider: number
}

interface ServiceCostEntry {
  service: ProvidedService
  type: number
  cost: number
}

interface EligibilityRequest {
  benefitProgramInstance: { program: number; year: number; revision: number }
  criteria: {
    householdSize: number
    liheapEligibleHouseholdSize: number
    averageMonthlyHouseholdIncome: number
    housingType: number
    housingStatus: number
    primaryHeatingService: ProvidedService | null
    liheapCost: number | null
    pseEnergyType: number | null
    serviceCosts: ServiceCostEntry[]
    providedService: ProvidedService[]
    useBackupServiceCostsForBenefitProgram: number[]
    benefitsRecieved: unknown[]
  }
}

interface EligibilityResponse {
  isEligible: boolean
  eligibleAmount: number
  details: Record<string, unknown> | null
  errors: string[]
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const HOUSING_TYPES = [
  { value: "0", label: "1–3 Family" },
  { value: "1", label: "4+ Family" },
  { value: "2", label: "Hi-Rise" },
  { value: "3", label: "Mobile" },
  { value: "4", label: "RV" },
  { value: "5", label: "RV w/ Propane" },
] as const

const HOUSING_STATUSES = [
  { value: "0", label: "Own" },
  { value: "1", label: "Rent" },
  { value: "2", label: "Subsidized" },
  { value: "3", label: "Rmr/Brdr" },
  { value: "4", label: "Temp Housing" },
] as const

const HEATING_SOURCES = [
  { value: "10", label: "Electricity", providerId: 1052 },
  { value: "11", label: "Gas", providerId: 1052 },
  { value: "12", label: "Propane", providerId: 0 },
  { value: "13", label: "Oil", providerId: 0 },
  { value: "14", label: "Wood/Pellets", providerId: 0 },
  { value: "15", label: "Coal", providerId: 0 },
] as const

const PSE_ENERGY_TYPES = [
  { value: "0", label: "All Electric" },
  { value: "1", label: "Gas and Electric" },
  { value: "2", label: "Gas Only" },
] as const

const PSE_PROGRAM_ID = 0
const LIHEAP_PROGRAM_ID = 1
const SHEAP_PROGRAM_ID = 3

// Which form sections each program needs
interface ProgramFields {
  householdAndIncome: boolean
  housing: boolean
  heatingSource: boolean
  energyCost: boolean
  pseFields: boolean
  supportsBackup: boolean
}

const PROGRAM_FIELDS: Record<number, ProgramFields> = {
  // PSE Help: needs income, housing (for backup), energy cost, PSE fields; no heating source
  0: { householdAndIncome: true, housing: true, heatingSource: false, energyCost: true, pseFields: true, supportsBackup: true },
  // LIHEAP: needs everything except PSE fields
  1: { householdAndIncome: true, housing: true, heatingSource: true, energyCost: true, pseFields: false, supportsBackup: true },
  // SHEAP: same as LIHEAP but no primary heating source needed
  3: { householdAndIncome: true, housing: true, heatingSource: false, energyCost: true, pseFields: false, supportsBackup: true },
  // LIHEAP Contingency: fixed $500, no criteria
  4: { householdAndIncome: false, housing: false, heatingSource: false, energyCost: false, pseFields: false, supportsBackup: false },
  // Arrears: checks if applied for LIHEAP (heating source)
  5: { householdAndIncome: false, housing: false, heatingSource: true, energyCost: false, pseFields: false, supportsBackup: false },
  // Rent Assistance: fixed $1000, no criteria
  6: { householdAndIncome: false, housing: false, heatingSource: false, energyCost: false, pseFields: false, supportsBackup: false },
  // Cooling Assistance: checks if applied for LIHEAP
  9: { householdAndIncome: false, housing: false, heatingSource: true, energyCost: false, pseFields: false, supportsBackup: false },
  // LIHEAP Supplemental: checks if applied for LIHEAP
  11: { householdAndIncome: false, housing: false, heatingSource: true, energyCost: false, pseFields: false, supportsBackup: false },
}

const DEFAULT_FIELDS: ProgramFields = {
  householdAndIncome: true, housing: true, heatingSource: true, energyCost: true, pseFields: false, supportsBackup: false,
}

// Detail field labels for each program type
const DETAIL_LABELS: Record<string, string> = {
  totalBackupCost: "Total Backup Cost",
  heatCostMultiplier: "Heat Cost Multiplier",
  annualHeatCost: "Annual Heat Cost",
  baseCost: "Base Cost",
  actualServiceCost: "Actual Service Cost",
  householdSizeAdjuster: "Household Size Adjuster",
  coefficientA: "Coefficient A",
  coefficientB: "Coefficient B",
  coefficientC: "Coefficient C",
  adjustedMonthlyHouseholdIncome: "Adjusted Monthly Income",
  intermediateStepA: "Intermediate Step A",
  intermediateStepB: "Intermediate Step B",
  intermediateStepC: "Intermediate Step C",
  intermediateStepD: "Intermediate Step D",
  housingTypeMultiplier: "Housing Type Multiplier",
  pseAdjustedAnnualHeatCost: "PSE Adjusted Annual Heat Cost",
  usedBackupCost: "Used Backup Cost",
  monthlyIncome: "Monthly Income",
  gasUsage: "Gas Usage",
  electricUsage: "Electric Usage",
  liheapCost: "LIHEAP Cost",
  recommendedGasAmount: "Recommended Gas Amount",
  recommendedElectricAmount: "Recommended Electric Amount",
  pseCost: "PSE Cost",
}

// Fields to skip in the detail breakdown
const SKIP_FIELDS = new Set([
  "$type",
  "isEligible",
  "eligibileAmount",
  "serviceCosts",
  "errors",
  "housingType",
  "housingStatus",
])

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeProvidedService(
  serviceId: number,
  providerId: number
): ProvidedService {
  return {
    service: { id: serviceId, categoryId: 100 },
    provider: providerId,
  }
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return "—"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "number") {
    if (Number.isInteger(value) && Math.abs(value) > 100) {
      return `$${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    }
    return value.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    })
  }
  return String(value)
}

function getDetailTypeName(type: string): string {
  const names: Record<string, string> = {
    liheap: "LIHEAP",
    pse_help: "PSE Help",
    sheap: "SHEAP",
    liheap_contingency: "LIHEAP Contingency",
    liheap_arrears: "Arrears",
    rent_assistance: "Rent Assistance",
    liheap_cooling_assistance: "Cooling Assistance",
    liheap_supplemental: "LIHEAP Supplemental",
    water_sewer: "Water/Sewer",
    lakehaven: "Lakehaven",
    not_eligible: "Not Eligible",
  }
  return names[type] ?? type
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function BenefitCalculatorPage() {
  // Program list from API
  const [programs, setPrograms] = useState<BenefitProgramSummary[]>([])
  const [loadingPrograms, setLoadingPrograms] = useState(true)
  const [loadedRevision, setLoadedRevision] = useState<PublishedRevision | null>(null)
  const [loadingRevision, setLoadingRevision] = useState(false)
  const [revisionError, setRevisionError] = useState<string | null>(null)

  // Form state
  const [programId, setProgramId] = useState("")
  const [year, setYear] = useState("")
  const [householdSize, setHouseholdSize] = useState("3")
  const [monthlyIncome, setMonthlyIncome] = useState("2500")
  const [housingType, setHousingType] = useState("0")
  const [housingStatus, setHousingStatus] = useState("0")
  const [heatingSource, setHeatingSource] = useState("11")
  const [annualEnergyCost, setAnnualEnergyCost] = useState("150")
  const [pseEnergyType, setPseEnergyType] = useState("1")
  const [liheapCost, setLiheapCost] = useState("250")
  const [useBackupCost, setUseBackupCost] = useState(false)

  // Result state
  const [result, setResult] = useState<EligibilityResponse | null>(null)
  const [calculating, setCalculating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const selectionGeneration = useRef(0)
  const calculationGeneration = useRef(0)
  const selectionKey = `${programId}:${year}`
  const currentSelectionKey = useRef(selectionKey)
  currentSelectionKey.current = selectionKey
  const selectedRevision = revisionForSelection(loadedRevision, selectionKey)

  // Derived state
  const selectedProgram = programs.find((p) => p.id === Number(programId))
  const availableYears = selectedProgram?.availableProgramYears ?? []
  const fields = useMemo(
    () => PROGRAM_FIELDS[Number(programId)] ?? DEFAULT_FIELDS,
    [programId]
  )
  const isPse = Number(programId) === PSE_PROGRAM_ID

  // Load programs on mount
  const loadPrograms = useCallback(async () => {
    try {
      setLoadingPrograms(true)
      const data =
        await api.get<BenefitProgramSummary[]>("/api/benefit-programs")
      setPrograms(data)
      // Default to first program with available years
      const first = data.find((p) => p.availableProgramYears.length > 0)
      if (first) {
        setProgramId(String(first.id))
        setYear(String(first.availableProgramYears[0]))
      }
    } catch {
      // Silently ignore — user can still select manually
    } finally {
      setLoadingPrograms(false)
    }
  }, [])

  useEffect(() => {
    loadPrograms()
  }, [loadPrograms])

  // Update year when program changes
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(Number(year))) {
      setYear(String(availableYears[0]))
    }
  }, [programId, availableYears, year])

  // Resolve the authoritative latest revision for the selected program/year. The
  // generation guard prevents a slower response for a previous selection from
  // enabling the calculator with the wrong revision.
  useEffect(() => {
    const generation = ++selectionGeneration.current
    calculationGeneration.current++
    // An old request's finally handler is deliberately ignored. Release its spinner here
    // so changing targets during a calculation cannot permanently disable the form.
    setCalculating(false)
    setResult(null)
    setError(null)
    setLoadedRevision(null)
    setRevisionError(null)

    if (!programId || !year || availableYears.length === 0 || !availableYears.includes(Number(year))) {
      setLoadingRevision(false)
      return
    }

    setLoadingRevision(true)
    api
      .get<ProgramYearSettingsMetadata>(
        `/api/benefit-programs/${programId}/settings?programYear=${encodeURIComponent(year)}`
      )
      .then((metadata) => {
        if (generation !== selectionGeneration.current) return
        setLoadedRevision(resolvePublishedRevision(metadata, programId, year))
      })
      .catch((err) => {
        if (generation !== selectionGeneration.current) return
        setLoadedRevision(null)
        setRevisionError(err instanceof Error ? err.message : "Unable to load the published revision.")
      })
      .finally(() => {
        if (generation === selectionGeneration.current) setLoadingRevision(false)
      })
  }, [programId, year, availableYears])

  const handleCalculate = async (e: React.FormEvent) => {
    e.preventDefault()
    if (selectedRevision === null) {
      setError("A published settings revision is required before calculating.")
      return
    }

    const generation = ++calculationGeneration.current
    const requestSelectionKey = selectionKey
    setCalculating(true)
    setError(null)
    setResult(null)

    const heatingSourceEntry = HEATING_SOURCES.find(
      (s) => s.value === heatingSource
    )
    const serviceId = Number(heatingSource)
    const providerId = heatingSourceEntry?.providerId ?? 0
    const progId = Number(programId)

    const serviceCosts: ServiceCostEntry[] = []
    if (fields.energyCost) {
      serviceCosts.push({
        service: makeProvidedService(serviceId, providerId),
        type: 0,
        cost: Number(annualEnergyCost),
      })
    }

    const request: EligibilityRequest = {
      benefitProgramInstance: {
        program: progId,
        year: Number(year),
        revision: selectedRevision,
      },
      criteria: {
        householdSize: fields.householdAndIncome ? Number(householdSize) : 1,
        liheapEligibleHouseholdSize: fields.householdAndIncome ? Number(householdSize) : 1,
        averageMonthlyHouseholdIncome: fields.householdAndIncome ? Number(monthlyIncome) : 0,
        housingType: fields.housing ? Number(housingType) : 0,
        housingStatus: fields.housing ? Number(housingStatus) : 0,
        primaryHeatingService: fields.heatingSource
          ? makeProvidedService(serviceId, providerId)
          : null,
        liheapCost: isPse ? Number(liheapCost) : null,
        pseEnergyType: isPse ? Number(pseEnergyType) : null,
        serviceCosts,
        providedService: [],
        useBackupServiceCostsForBenefitProgram:
          useBackupCost && fields.supportsBackup ? [progId] : [],
        benefitsRecieved: [],
      },
    }

    try {
      const response = await api.post<EligibilityResponse>(
        "/api/benefit-calculations/eligibility",
        request
      )
      if (requestSelectionKey === currentSelectionKey.current && generation === calculationGeneration.current) {
        setResult(response)
      }
    } catch (err) {
      if (generation === calculationGeneration.current) {
        setError(err instanceof Error ? err.message : "Calculation failed")
      }
    } finally {
      if (generation === calculationGeneration.current) setCalculating(false)
    }
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      {/* Header */}
      <div className="flex items-center gap-2">
        <IconCalculator className="size-6" />
        <h1 className="text-2xl font-bold">Benefit Calculator</h1>
      </div>
      <p className="text-muted-foreground text-sm -mt-2">
        Estimate benefit eligibility and amounts using program settings.
      </p>

      <div className="grid gap-6 lg:grid-cols-[1fr_1fr]">
        {/* ── Form Card ────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Eligibility Criteria</CardTitle>
            <CardDescription>
              Enter household information to calculate benefit eligibility.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCalculate} className="grid gap-5">
              {/* Program & Year */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="program">Benefit Program</Label>
                  <Select
                    value={programId}
                    onValueChange={setProgramId}
                    disabled={loadingPrograms}
                  >
                    <SelectTrigger id="program" className="w-full">
                      <SelectValue placeholder="Select program…" />
                    </SelectTrigger>
                    <SelectContent>
                      {programs
                        .filter((p) => p.availableProgramYears.length > 0)
                        .map((p) => (
                          <SelectItem key={p.id} value={String(p.id)}>
                            {p.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="year">Program Year</Label>
                  <Select
                    value={year}
                    onValueChange={setYear}
                    disabled={availableYears.length === 0}
                  >
                    <SelectTrigger id="year" className="w-full">
                      <SelectValue placeholder="Year" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableYears.map((y) => (
                        <SelectItem key={y} value={String(y)}>
                          {y}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground" aria-live="polite">
                    {loadingRevision
                      ? "Loading published revision…"
                      : selectedRevision === null
                        ? "No published revision available"
                        : `Published revision ${selectedRevision}`}
                  </p>
                </div>
              </div>

              <Separator />

              {/* Household — shown for programs that need income check */}
              {fields.householdAndIncome && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="household-size">Household Size</Label>
                    <Input
                      id="household-size"
                      type="number"
                      min={1}
                      max={24}
                      required
                      value={householdSize}
                      onChange={(e) => setHouseholdSize(e.target.value)}
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="monthly-income">Monthly Income ($)</Label>
                    <Input
                      id="monthly-income"
                      type="number"
                      min={0}
                      step="0.01"
                      required
                      value={monthlyIncome}
                      onChange={(e) => setMonthlyIncome(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Housing — shown for LIHEAP, PSE, SHEAP */}
              {fields.housing && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="housing-type">Housing Type</Label>
                    <Select value={housingType} onValueChange={setHousingType}>
                      <SelectTrigger id="housing-type" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOUSING_TYPES.map((h) => (
                          <SelectItem key={h.value} value={h.value}>
                            {h.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="housing-status">Housing Status</Label>
                    <Select
                      value={housingStatus}
                      onValueChange={setHousingStatus}
                    >
                      <SelectTrigger id="housing-status" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HOUSING_STATUSES.map((h) => (
                          <SelectItem key={h.value} value={h.value}>
                            {h.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Energy section separator */}
              {(fields.heatingSource || fields.energyCost) && <Separator />}

              {/* Primary Heating Source — LIHEAP, Arrears, Cooling, Supplemental */}
              {fields.heatingSource && !fields.energyCost && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="heating-source">
                      Primary Heating Source
                    </Label>
                    <Select
                      value={heatingSource}
                      onValueChange={setHeatingSource}
                    >
                      <SelectTrigger id="heating-source" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HEATING_SOURCES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Heating Source + Energy Cost — LIHEAP */}
              {fields.heatingSource && fields.energyCost && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="heating-source">
                      Primary Heating Source
                    </Label>
                    <Select
                      value={heatingSource}
                      onValueChange={setHeatingSource}
                    >
                      <SelectTrigger id="heating-source" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {HEATING_SOURCES.map((s) => (
                          <SelectItem key={s.value} value={s.value}>
                            {s.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="annual-energy-cost">
                      Annual Energy Cost ($)
                    </Label>
                    <Input
                      id="annual-energy-cost"
                      type="number"
                      min={0}
                      step="0.01"
                      required
                      aria-describedby="annual-energy-cost-help"
                      value={annualEnergyCost}
                      onChange={(e) => setAnnualEnergyCost(e.target.value)}
                    />
                    <p id="annual-energy-cost-help" className="text-xs text-muted-foreground">
                      Enter the total for the 12-month period, not a monthly amount.
                    </p>
                  </div>
                </div>
              )}

              {/* Energy Cost only (no heating source) — PSE, SHEAP */}
              {!fields.heatingSource && fields.energyCost && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="annual-energy-cost">
                      Annual Energy Cost ($)
                    </Label>
                    <Input
                      id="annual-energy-cost"
                      type="number"
                      min={0}
                      step="0.01"
                      required
                      aria-describedby="annual-energy-cost-help"
                      value={annualEnergyCost}
                      onChange={(e) => setAnnualEnergyCost(e.target.value)}
                    />
                    <p id="annual-energy-cost-help" className="text-xs text-muted-foreground">
                      Enter the total for the 12-month period, not a monthly amount.
                    </p>
                  </div>
                </div>
              )}

              {/* PSE-specific fields */}
              {fields.pseFields && (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="grid gap-2">
                    <Label htmlFor="pse-energy-type">PSE Energy Type</Label>
                    <Select
                      value={pseEnergyType}
                      onValueChange={setPseEnergyType}
                    >
                      <SelectTrigger id="pse-energy-type" className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PSE_ENERGY_TYPES.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="liheap-cost">LIHEAP Cost ($)</Label>
                    <Input
                      id="liheap-cost"
                      type="number"
                      min={0}
                      step="0.01"
                      value={liheapCost}
                      onChange={(e) => setLiheapCost(e.target.value)}
                    />
                  </div>
                </div>
              )}

              {/* Use Backup Cost checkbox */}
              {fields.supportsBackup && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="use-backup-cost"
                    checked={useBackupCost}
                    onCheckedChange={(checked) =>
                      setUseBackupCost(checked === true)
                    }
                  />
                  <Label
                    htmlFor="use-backup-cost"
                    className="text-sm font-normal cursor-pointer"
                  >
                    Use backup energy costs (predefined usage amounts instead of
                    actual costs)
                  </Label>
                </div>
              )}

              {/* Error */}
              {(error || revisionError) && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {error ?? revisionError}
                </div>
              )}

              {/* Submit */}
              <Button
                type="submit"
                className="w-full sm:w-auto"
                disabled={calculating || loadingRevision || selectedRevision === null || !programId || !year}
              >
                {calculating ? (
                  <IconLoader2 className="size-4 animate-spin" />
                ) : (
                  <IconCalculator className="size-4" />
                )}
                {calculating ? "Calculating…" : "Calculate Benefit"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* ── Results Card ─────────────────────────────────────── */}
        <div className="flex flex-col gap-4">
          {result && (
            <>
              {/* Summary */}
              <Card
                className={
                  result.isEligible
                    ? "border-green-200 dark:border-green-900"
                    : "border-red-200 dark:border-red-900"
                }
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">Result</CardTitle>
                    <Badge
                      variant={result.isEligible ? "default" : "destructive"}
                      className={
                        result.isEligible
                          ? "bg-green-600 hover:bg-green-700"
                          : ""
                      }
                    >
                      {result.isEligible ? (
                        <IconCheck className="size-3 mr-1" />
                      ) : (
                        <IconX className="size-3 mr-1" />
                      )}
                      {result.isEligible ? "Eligible" : "Not Eligible"}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <IconCurrencyDollar className="size-8 text-muted-foreground" />
                    <span className="text-4xl font-bold tabular-nums">
                      {result.eligibleAmount.toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                  {selectedProgram && (
                    <p className="text-sm text-muted-foreground mt-2">
                      {selectedProgram.name} — {year} (revision {selectedRevision})
                    </p>
                  )}
                  {result.errors.length > 0 && (
                    <div className="mt-3 rounded-md bg-destructive/10 p-3">
                      <p className="text-sm font-medium text-destructive mb-1">
                        Errors
                      </p>
                      <ul className="text-sm text-destructive list-disc pl-4">
                        {result.errors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Calculation Breakdown */}
              {result.details && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">
                      Calculation Breakdown
                    </CardTitle>
                    {!!result.details["$type"] && (
                      <CardDescription>
                        {getDetailTypeName(
                          result.details["$type"] as string
                        )}{" "}
                        formula
                      </CardDescription>
                    )}
                  </CardHeader>
                  <CardContent className="p-0">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Parameter</TableHead>
                          <TableHead className="text-right">Value</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(result.details)
                          .filter(
                            ([key, val]) =>
                              !SKIP_FIELDS.has(key) &&
                              val !== null &&
                              val !== undefined &&
                              typeof val !== "object"
                          )
                          .map(([key, val]) => (
                            <TableRow key={key}>
                              <TableCell className="text-muted-foreground text-sm">
                                {DETAIL_LABELS[key] ?? key}
                              </TableCell>
                              <TableCell className="text-right font-mono text-sm">
                                {formatValue(val)}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {/* Empty state */}
          {!result && !calculating && (
            <Card className="flex flex-col items-center justify-center py-16 text-center">
              <IconCalculator className="size-12 text-muted-foreground/40 mb-3" />
              <p className="text-muted-foreground text-sm">
                Fill in the criteria and click Calculate to see results.
              </p>
            </Card>
          )}

          {/* Loading state */}
          {calculating && (
            <Card className="flex flex-col items-center justify-center py-16">
              <IconLoader2 className="size-8 animate-spin text-muted-foreground mb-3" />
              <p className="text-muted-foreground text-sm">
                Running calculation…
              </p>
            </Card>
          )}
        </div>
      </div>
    </div>
  )
}
